import React, { useState } from 'react';
import TurnstileWidget from './TurnstileWidget';
import { API_BASE_URL, TURNSTILE_SITE_KEY } from '../config';

const MAP_LAT = 20.583247022675213;
const MAP_LNG = -100.40186154631137;

const ContactSection = () => {
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileKey, setTurnstileKey] = useState(0);

  const resetTurnstile = () => {
    setTurnstileToken('');
    setTurnstileKey((key) => key + 1);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setStatus('error');
      setErrorMessage('Completa la verificación de seguridad.');
      return;
    }

    const form = event.currentTarget;
    const payload = Object.fromEntries(new FormData(form).entries());
    payload.turnstile_token = turnstileToken;

    setStatus('sending');
    setErrorMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/contacto-publico/enviar/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setStatus('error');
        setErrorMessage(
          response.status === 429
            ? 'Has enviado varios mensajes seguidos. Intenta más tarde o escríbenos a info@eccompliance.com.'
            : data.error || 'No se pudo enviar tu mensaje. Intenta de nuevo.'
        );
      } else {
        setStatus('success');
        form.reset();
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage('No se pudo enviar tu mensaje. Revisa tu conexión o escríbenos a info@eccompliance.com.');
    }
    resetTurnstile();
  };

  return (
    <section id="contacto" className="w-full py-12 md:py-24 lg:py-32 bg-white dark:bg-gray-950">
      <div className="container px-4 md:px-6">
        <div className="mx-auto max-w-3xl space-y-6 text-center">
          <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl text-gray-900 dark:text-gray-100">
            Contáctanos
          </h2>
          <p className="text-gray-600 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed dark:text-gray-400">
            Completa el formulario y nos pondremos en contacto contigo a la brevedad.
          </p>
          <form onSubmit={handleSubmit} className="relative space-y-6 p-6 bg-gray-100 dark:bg-gray-900 rounded-lg shadow-lg">
            {/* Honeypot for bots: hidden from people and screen readers. */}
            <div className="absolute -left-[9999px] h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor="sitio_web">Sitio web</label>
              <input id="sitio_web" name="sitio_web" type="text" tabIndex={-1} autoComplete="off" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 text-left">
                <label htmlFor="name" className="text-sm font-medium text-gray-700 dark:text-gray-300">Nombre</label>
                <input
                  id="name"
                  name="nombre"
                  required
                  maxLength={150}
                  autoComplete="name"
                  className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:ring-offset-gray-950 dark:placeholder:text-gray-500 dark:focus-visible:ring-teal-300"
                  placeholder="Tu nombre completo"
                  type="text"
                />
              </div>
              <div className="space-y-2 text-left">
                <label htmlFor="email" className="text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                <input
                  id="email"
                  name="email"
                  required
                  autoComplete="email"
                  className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:ring-offset-gray-950 dark:placeholder:text-gray-500 dark:focus-visible:ring-teal-300"
                  placeholder="tu.email@ejemplo.com"
                  type="email"
                />
              </div>
            </div>
            <div className="space-y-2 text-left">
              <label htmlFor="service" className="text-sm font-medium text-gray-700 dark:text-gray-300">Servicio de Interés</label>
              <select
                id="service"
                name="servicio"
                className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:ring-offset-gray-950 dark:focus-visible:ring-teal-300"
              >
                <option value="">Selecciona un servicio</option>
                <option value="asesoria_aduanal">Asesoría Aduanal</option>
                <option value="anexo_24_30">Anexo 24 y 30</option>
                <option value="expediente_digital">Expediente Digital VUCEM</option>
                <option value="manifestacion_valor">Manifestación de Valor Electrónica (MVE)</option>
                <option value="agencia_aduanal">Agencia Aduanal</option>
                <option value="logistica_transporte">Logística y Transporte</option>
                <option value="integracion_sistemas">Integración de Sistemas</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <div className="space-y-2 text-left">
              <label htmlFor="subject" className="text-sm font-medium text-gray-700 dark:text-gray-300">Asunto</label>
              <input
                id="subject"
                name="asunto"
                maxLength={200}
                className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:ring-offset-gray-950 dark:placeholder:text-gray-500 dark:focus-visible:ring-teal-300"
                placeholder="Motivo de tu consulta"
                type="text"
              />
            </div>
            <div className="space-y-2 text-left">
              <label htmlFor="message" className="text-sm font-medium text-gray-700 dark:text-gray-300">Mensaje</label>
              <textarea
                id="message"
                name="mensaje"
                required
                maxLength={5000}
                className="flex min-h-[120px] w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm ring-offset-white placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:ring-offset-gray-950 dark:placeholder:text-gray-500 dark:focus-visible:ring-teal-300 resize-y"
                placeholder="Escribe tu mensaje aquí..."
              ></textarea>
            </div>
            <TurnstileWidget key={turnstileKey} onToken={setTurnstileToken} />
            {status === 'success' && (
              <p role="status" className="text-sm font-medium text-green-600 dark:text-green-400">
                Gracias, recibimos tu mensaje. Te responderemos a la brevedad.
              </p>
            )}
            {status === 'error' && (
              <p role="alert" className="text-sm font-medium text-red-600 dark:text-red-400">{errorMessage}</p>
            )}
            <button
              type="submit"
              disabled={status === 'sending'}
              className="inline-flex h-10 items-center justify-center rounded-md bg-indigo-700 px-8 text-sm font-medium text-white shadow transition-colors hover:bg-indigo-800 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-700 disabled:pointer-events-none disabled:opacity-50 dark:bg-teal-500 dark:text-gray-900 dark:hover:bg-teal-600 dark:focus-visible:ring-teal-300"
            >
              {status === 'sending' ? 'Enviando…' : 'Enviar Mensaje'}
            </button>
          </form>
          <div className="flex flex-wrap justify-center gap-4 mt-4">
            <a 
              href="https://wa.me/524421424827" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-flex h-10 items-center justify-center rounded-md bg-green-500 px-8 text-sm font-medium text-white shadow transition-colors hover:bg-green-600 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-green-500 disabled:pointer-events-none disabled:opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
              </svg>
              Enviar WhatsApp
            </a>
            <a 
              href="tel:+524421424827" 
              className="inline-flex h-10 items-center justify-center rounded-md bg-green-600 px-8 text-sm font-medium text-white shadow transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-green-600 disabled:pointer-events-none disabled:opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2H7c.45 0 .9.06 1.3.18a2 2 0 0 1 1.41.46L10.9 9.1a2 2 0 0 1-.45 2.11L8.09 13.91a16 16 0 0 0 6 6l1.72-1.72a2 2 0 0 1 2.11-.45l3.43 1.43a2 2 0 0 1 .46 1.4z"></path>
              </svg>
              Llamar Ahora
            </a>
          </div>
          <div className="w-full mt-8 overflow-hidden rounded-lg border border-gray-300 shadow-md dark:border-gray-700">
            <iframe
              title="Ubicación de EC-Compliance en el mapa"
              src={`https://www.google.com/maps?q=${MAP_LAT},${MAP_LNG}&z=16&output=embed`}
              className="block w-full h-64 md:h-80"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <address className="mt-4 not-italic text-center text-sm text-gray-600 dark:text-gray-400">
            Del Mesón 184, Col. Del Prado, 76030 Santiago de Querétaro, Qro.
            <br />
            <a href="tel:+524421424827" className="hover:underline">+52 (442) 142 4827</a>
            {' · '}
            <a href="mailto:info@eccompliance.com" className="hover:underline">info@eccompliance.com</a>
          </address>
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${MAP_LAT},${MAP_LNG}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-block text-sm font-medium text-indigo-700 hover:underline dark:text-teal-400"
          >
            Cómo llegar
          </a>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
